/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

import React, { ReactNode } from 'react';
import { FormattedMessage, WrappedComponentProps, injectIntl } from 'react-intl';
import { ConnectedProps, connect } from 'react-redux';
import SearchUtils from '../../../utils/SearchUtils';
import Carousel from '@cchome/carousel';
import './SearchDropDownQuickActions.scss';
import HorizontalRuler from '../../HorizontalRuler';
import SearchAnalytics from '../../../analytics/SearchAnalytics';
import SearchHostContext from '../../../utils/SearchHostContext';
import SearchDropDownQuickActionsItem from './SearchDropDownQuickActionsItem';
import ClickBase from '../../../containers/SearchResultsPage/ClickBase';
import AnalyticsUtils from '../../../analytics/AnalyticsUtil';
import {
	APP_TYPES,
	PRESEARCH_UI_SORT_ORDERS,
	SEARCH_CCD_CCH_SAMCAP_CGEN,
} from '../../../containers/SearchResultsPage/SearchConstants';

type OwnProps = {};

type SearchDropDownQuickActionsProps = OwnProps & WrappedComponentProps & PropsFromRedux;

class SearchDropDownQuickActions extends ClickBase<SearchDropDownQuickActionsProps> {
	analytics: SearchAnalytics;

	constructor(props) {
		super(props);
		this.searchHostContext = SearchHostContext.getContext();
		this.analytics = new SearchAnalytics(this.searchHostContext);
		this.onCarouselClick = this.onCarouselClick.bind(this);
		this.onClickSeeAll = this.onClickSeeAll.bind(this);
	}

	componentDidMount(): void {
		const quickActionsData =
			this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.resultItems || [];
		const applicableQuicActionsData = this.getApplicableQuickActionData(quickActionsData);
		if (!applicableQuicActionsData || !applicableQuicActionsData.length) {
			return;
		}
	}
	getApplicableQuickActionData = (quickActionsData) =>
		// Show only if we have icon and action url
		quickActionsData.filter((quickAction) => quickAction.enabled && quickAction.id && quickAction.actionUrl);

	onClickSeeAll = () => {
		let viewAllUrl = this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.viewAll;
		if (!viewAllUrl) {
			return;
		}
		const {
			hostInfo: { surface, client },
		} = this.searchHostContext;
		let trackingParams = '';
		if (surface === SEARCH_CCD_CCH_SAMCAP_CGEN.ccdHost) {
			trackingParams = `${SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForPreSearchQuickActionViewAll}&${SEARCH_CCD_CCH_SAMCAP_CGEN.samcapId}`;
		} else if (client === SEARCH_CCD_CCH_SAMCAP_CGEN.cchClient) {
			trackingParams = SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForPreSearchQuickActionViewAll;
		}

		viewAllUrl = viewAllUrl.includes('?') ? `${viewAllUrl}&${trackingParams}` : `${viewAllUrl}?${trackingParams}`;
		AnalyticsUtils.location = 'Search:view-all:Popular Adobe Express QuickActions';
		AnalyticsUtils.locationVersion = AnalyticsUtils.searchComponentVersion;
		viewAllUrl = AnalyticsUtils.appendUrlWithAnalytics(viewAllUrl);
		this.analytics.postSeeAll('popularExpressQuickActions', viewAllUrl);
		this.onClickShowAllBase(this.searchHostContext, APP_TYPES.SPARK_TEMPLATES, {
			openUrl: viewAllUrl,
		});
	};
	renderTitle = () => {
		const headerTextId = 'app.search.presearch.quickActions.title';

		return (
			<div className='search-dropdown-quick-action-title-container'>
				<span className='search-dropdown-quick-actions-title'>
					<h5>
						<FormattedMessage id={headerTextId} />
					</h5>
				</span>
			</div>
		);
	};

	renderViewAll = () => {
		const viewAllUrl = this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.viewAll;
		const translationId = 'app.search.see_more';
		return (
			viewAllUrl && (
				<span
					className='see-all-text'
					onKeyDown={(event) => SearchUtils.onKeyDown(event)}
					tabIndex={0}
					onClick={this.onClickSeeAll}
					role='link'
				>
					<a role='navigation' className='link-out-tag'>
						<span className='all-text'>
							<FormattedMessage id={translationId} />
						</span>
					</a>
				</span>
			)
		);
	};

	onCarouselClick = (e: any) => {
		this.analytics.postPreSearchCarouselScroll(e, 'popularExpressQuickActions');
	};

	renderQuickActions = () => {
		const quickActionsData =
			this.props.presearchResultsPayload?.expressQuickActions?.searchResults?.resultItems || [];
		const applicableQuicActionsData = this.getApplicableQuickActionData(quickActionsData);
		if (!applicableQuicActionsData || !applicableQuicActionsData.length) {
			return null;
		}

		this.analytics.postPreSearchItemAction({
			eventType: 'render',
			recommendationType: 'popularExpressQuickActions',
			uiSortOrder: PRESEARCH_UI_SORT_ORDERS.popularExpressQuickActions,
			data: applicableQuicActionsData,
		});
		const quickActionItems = applicableQuicActionsData.map((quickAction, index) => (
			<SearchDropDownQuickActionsItem
				key={quickAction.id}
				data={quickAction}
				index={index}
				count={applicableQuicActionsData.length}
				lastCard={index === applicableQuicActionsData.length - 1}
			/>
		));

		return (
			<>
				<div className='search-dropdown-quick-action-container'>
					<div className='search-dropdown-quick-action-header text-container'>
						{this.renderTitle()}
						{this.renderViewAll()}
					</div>
					<div className='search-dropdown-quick-action'>
						<Carousel
							isCCX={SearchUtils.applyBorderBox()}
							darkMode={SearchUtils.isDarkMode(this.props.theme)}
							actionEvents={this.onCarouselClick}
							keepWidth
						>
							{quickActionItems}
						</Carousel>
					</div>
				</div>
				<HorizontalRuler />
			</>
		);
	};

	render(): ReactNode | null {
		return this.renderQuickActions();
	}
}

const mapStateToProps = ({ search }: RootState) => ({
	theme: search.theme,
	presearchResultsPayload: search.presearchResultsPayload,
	searchAppMetadataList: search.searchAppMetadataList,
});
const mapDispatchToProps = {};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(injectIntl(SearchDropDownQuickActions));
